import { compact } from 'lodash';
import {
  ConfiguredProduct,
  ConfigurableProduct,
  ConfiguredProductBundle,
  ProductBundleSummary,
  ProductSummaryWithFinishGroups,
  ConfiguredProductCharacteristics,
  SerializedConfiguredProduct,
  SerializedConfiguredProductBundle,
} from '@/types/product';
import { isBundle } from '@/utils/product';

export const defaultCharacteristics: ConfiguredProductCharacteristics = [
  null,
  null,
  null,
  null,
];

const configureDefaultProduct = (
  product: ProductSummaryWithFinishGroups
): ConfiguredProduct => {
  return {
    sku: product.sku,
    quantity: 1,
    characteristics:
      (product.pim?.characteristics?.map((characteristic, index) =>
        characteristic?.code
          ? (product.finishGroups[index].finishes?.[0]?.code ?? null)
          : null
      ) as ConfiguredProductCharacteristics) ?? defaultCharacteristics,
  };
};

export const configureDefaultProductBundleOption = (
  product: ConfigurableProduct
): ConfiguredProduct => {
  return {
    sku: product.sku,
    quantity: 1,
    characteristics: product.finishGroups.map(
      (finishGroup) => finishGroup?.finishes?.[0]?.code ?? null
    ) as ConfiguredProductCharacteristics,
  };
};

export const configureDefaultProductBundle = (
  product: ProductBundleSummary
): ConfiguredProductBundle => {
  return {
    sku: product.sku,
    quantity: 1,
    options:
      product.bundleOptions?.map((option) => {
        const firstProduct =
          option.products.find((product) => product.isDefault) ?? option.products[0];
        return firstProduct ? configureDefaultProductBundleOption(firstProduct) : null;
      }) ?? [],
  };
};

export const makeDefaultConfiguration = (
  product: ProductSummaryWithFinishGroups | ProductBundleSummary
): ConfiguredProduct | ConfiguredProductBundle => {
  return isBundle(product)
    ? configureDefaultProductBundle(product)
    : configureDefaultProduct(product);
};

export const hasAvailableOptions = (product: ProductBundleSummary) => {
  const options = product?.bundleOptions;
  const skus =
    options
      ?.map((option) => compact(option.products.map((product) => product.sku)).flat())
      .flat() ?? [];

  return skus.length > 0;
};

const isConfigurableBundle = (
  product: ProductSummaryWithFinishGroups | ProductBundleSummary
) => isBundle(product) && hasAvailableOptions(product) && product?.pim?.configurator;

const isConfigurableSingle = (
  product: ProductSummaryWithFinishGroups | ProductBundleSummary | null
) =>
  !!(product?.pim?.configurator && product?.pim?.characteristics) &&
  product?.pim?.configurator;

export const isConfigurable = (
  product: ProductSummaryWithFinishGroups | ProductBundleSummary | null
) => (isBundle(product) ? isConfigurableBundle(product) : isConfigurableSingle(product));

export const isConfiguredBundle = (
  product: ConfiguredProduct | ConfiguredProductBundle
): product is ConfiguredProductBundle => 'options' in product;

export const isConfiguredEqualSingle = (a: ConfiguredProduct, b: ConfiguredProduct) =>
  a.sku === b.sku &&
  a.characteristics.every(
    (_, index) => a.characteristics[index] === b.characteristics[index]
  );

export const isConfiguredEqualBundle = (
  a: ConfiguredProductBundle,
  b: ConfiguredProductBundle
) =>
  a.sku === b.sku &&
  a.options.every(
    (option, index) =>
      (option === null && b.options[index] === null) ||
      isConfiguredEqualSingle(option!, b.options[index]!)
  );

export const isConfiguredEqual = (
  a: ConfiguredProduct | ConfiguredProductBundle,
  b: ConfiguredProduct | ConfiguredProductBundle
) => {
  if (isConfiguredBundle(a) && isConfiguredBundle(b)) {
    return isConfiguredEqualBundle(a, b);
  }

  if (!isConfiguredBundle(a) && !isConfiguredBundle(b)) {
    return isConfiguredEqualSingle(a, b);
  }

  return false;
};

export const COALESCING_CHARACTERISTIC_CODE = 'N00';
export const serializeConfiguredProductCharacteristics = (
  characteristics: ConfiguredProductCharacteristics
) =>
  characteristics
    .map((characteristic) => characteristic ?? COALESCING_CHARACTERISTIC_CODE)
    .map((characteristic) => characteristic.padStart(3, '0').slice(-3))
    .join('');

export const serializeConfiguredProduct = (
  product: ConfiguredProduct
): SerializedConfiguredProduct => {
  return `${product.sku}${serializeConfiguredProductCharacteristics(product.characteristics)}`;
};

export const serializeConfiguredProductBundle = (
  product: ConfiguredProductBundle
): SerializedConfiguredProductBundle => {
  const options = product.options.map((option) =>
    option ? serializeConfiguredProduct(option) : COALESCING_CHARACTERISTIC_CODE
  );
  return [product.sku, options];
};
